// frontend/src/components/VehicleDocumentsCard.js
//
// Assurance et visite technique, on the workshop's page for a car.
//
// The garage is the party holding the papers — it reads the attestation and
// the vignette off the car while it is in the bay — so it is the one best
// placed to keep these two dates current. PATCH /api/vehicles/:id accepts
// exactly `insurance` and `technicalCheck` from an assigned workshop (plus the
// odometer); this is the screen that sends them.
//
// The expiry is the point, not the company name: a car whose assurance lapsed
// last week should say so in red before anybody drives it off the forecourt.

import React, { useState } from 'react';
import axios from 'axios';
import API_URL from '../api/baseURL';

const DAY = 86400000;

/** Days until expiry — negative once it has lapsed. */
function daysLeft(date) {
  if (!date) return null;
  const t = new Date(date);
  if (Number.isNaN(t.getTime())) return null;
  // Compare whole days, so "expires today" is 0 rather than a few hours.
  const end = new Date(t.getFullYear(), t.getMonth(), t.getDate());
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((end - today) / DAY);
}

/**
 * Three states, and the middle one is the useful one: a document expiring in
 * three weeks is something to mention to the client while they are standing
 * there, not something to discover after it lapses.
 */
function statusOf(date) {
  const d = daysLeft(date);
  if (d === null) return { tone: 'none', label: 'non renseignée', color: '#6E665C' };
  if (d < 0) return { tone: 'expired', label: `expirée depuis ${Math.abs(d)} j`, color: '#E2574B' };
  if (d === 0) return { tone: 'expired', label: "expire aujourd'hui", color: '#E2574B' };
  if (d <= 30) return { tone: 'soon', label: `expire dans ${d} j`, color: '#DFA23C' };
  return { tone: 'ok', label: `valide ${d} j`, color: '#74A98D' };
}

const fmtDate = (d) => {
  if (!d) return '—';
  const t = new Date(d);
  return Number.isNaN(t.getTime()) ? '—' : t.toLocaleDateString('fr-FR');
};

/** yyyy-mm-dd for <input type="date">, in local time. */
const toInputDate = (d) => {
  if (!d) return '';
  const t = new Date(d);
  if (Number.isNaN(t.getTime())) return '';
  const p = (n) => String(n).padStart(2, '0');
  return `${t.getFullYear()}-${p(t.getMonth() + 1)}-${p(t.getDate())}`;
};

const label = 'text-[10px] uppercase tracking-[.18em] text-[#6E665C]';
const input =
  'w-full h-[34px] px-2.5 rounded-[8px] border border-[#2B2724] bg-[#100F0D] text-[#F4F1EA] ' +
  'text-[12.5px] outline-none focus:border-[#4E4740] placeholder:text-[#5C554C]';

function DocRow({ title, company, expiry }) {
  const st = statusOf(expiry);
  return (
    <div className="flex items-baseline justify-between gap-4 py-1.5">
      <div className="min-w-0">
        <p className={label}>{title}</p>
        <p className="text-[12.5px] text-[#F4F1EA] mt-0.5 truncate">{company || '—'}</p>
      </div>
      <div className="text-right shrink-0">
        <p className="font-['Space_Grotesk',sans-serif] tabular-nums text-[12.5px] text-[#C9C3B6]">
          {fmtDate(expiry)}
        </p>
        <p className="text-[10.5px] mt-0.5" style={{ color: st.color }}>{st.label}</p>
      </div>
    </div>
  );
}

export default function VehicleDocumentsCard({ vehicle, token, canEdit = false, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [form, setForm] = useState({
    insuranceCompany: vehicle?.insurance?.company || '',
    insuranceExpiry: toInputDate(vehicle?.insurance?.expiry),
    technicalCompany: vehicle?.technicalCheck?.company || '',
    technicalExpiry: toInputDate(vehicle?.technicalCheck?.expiry),
  });

  if (!vehicle) return null;

  const open = () => {
    // Re-read from the vehicle each time, so a save elsewhere is not
    // overwritten by a stale draft from the last time the form was opened.
    setForm({
      insuranceCompany: vehicle.insurance?.company || '',
      insuranceExpiry: toInputDate(vehicle.insurance?.expiry),
      technicalCompany: vehicle.technicalCheck?.company || '',
      technicalExpiry: toInputDate(vehicle.technicalCheck?.expiry),
    });
    setErr('');
    setEditing(true);
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setSaving(true);
    setErr('');
    try {
      // Exactly the two fields the API accepts from a workshop. Sending more
      // would earn a 403 naming them — see utils/vehicleUpdateFields.
      const body = {
        insurance: {
          company: form.insuranceCompany.trim(),
          expiry: form.insuranceExpiry || null,
        },
        technicalCheck: {
          company: form.technicalCompany.trim(),
          expiry: form.technicalExpiry || null,
        },
      };
      const res = await axios.patch(`${API_URL}/vehicles/${vehicle._id}`, body, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setEditing(false);
      if (onSaved) onSaved(res.data?.vehicle || res.data);
    } catch (e) {
      setErr(e.response?.data?.error || 'Enregistrement impossible');
    } finally {
      setSaving(false);
    }
  };

  const worst = [vehicle.insurance?.expiry, vehicle.technicalCheck?.expiry]
    .map(statusOf)
    .sort((a, b) => ['expired', 'soon', 'none', 'ok'].indexOf(a.tone)
      - ['expired', 'soon', 'none', 'ok'].indexOf(b.tone))[0];

  return (
    <section
      className="rounded-2xl border bg-gradient-to-b from-[#17140F] to-[#131110] overflow-hidden mb-6"
      // The border carries the warning: an expired document should be visible
      // before the card is read.
      style={{ borderColor: worst.tone === 'expired' ? 'rgba(226,87,75,.35)'
        : worst.tone === 'soon' ? 'rgba(223,162,60,.30)' : '#241F1B' }}
    >
      <header className="flex items-center justify-between gap-3 px-[18px] py-3 border-b border-[#241F1B]">
        <p className="text-[10.5px] uppercase tracking-[.16em] text-[#6E665C]">
          Assurance et visite technique
        </p>
        {canEdit && !editing && (
          <button
            onClick={open}
            className="h-[26px] px-2.5 rounded-[7px] border border-[#2B2724] hover:border-[#4E4740]
                       text-[11px] text-[#8F877B] hover:text-[#F4F1EA] transition-colors"
          >
            Modifier
          </button>
        )}
      </header>

      <div className="p-[18px]">
        {!editing ? (
          <>
            <DocRow
              title="Assurance"
              company={vehicle.insurance?.company}
              expiry={vehicle.insurance?.expiry}
            />
            <div className="border-t border-[#1E1B18] my-1" />
            <DocRow
              title="Visite technique"
              company={vehicle.technicalCheck?.company}
              expiry={vehicle.technicalCheck?.expiry}
            />
            {!canEdit && (
              // Said plainly rather than just hiding the button: a garage that
              // cannot edit should know it is because the car is not linked to
              // it, not assume the feature is missing.
              <p className="text-[10.5px] text-[#5C554C] mt-2.5">
                Véhicule non rattaché à votre atelier — lecture seule.
              </p>
            )}
          </>
        ) : (
          <div className="flex flex-col gap-3">
            <div className="grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(180px,1fr))]">
              <label className="block">
                <span className={`${label} block mb-1`}>Compagnie d'assurance</span>
                <input className={input} value={form.insuranceCompany}
                       onChange={set('insuranceCompany')} placeholder="NSIA, Allianz…" />
              </label>
              <label className="block">
                <span className={`${label} block mb-1`}>Expiration assurance</span>
                <input type="date" className={input} value={form.insuranceExpiry}
                       onChange={set('insuranceExpiry')} />
              </label>
              <label className="block">
                <span className={`${label} block mb-1`}>Centre de visite technique</span>
                <input className={input} value={form.technicalCompany}
                       onChange={set('technicalCompany')} placeholder="SICTA…" />
              </label>
              <label className="block">
                <span className={`${label} block mb-1`}>Expiration visite technique</span>
                <input type="date" className={input} value={form.technicalExpiry}
                       onChange={set('technicalExpiry')} />
              </label>
            </div>

            {err && <p className="text-[11px] text-[#E2574B]">{err}</p>}

            <div className="flex justify-end gap-2.5">
              <button
                onClick={() => { setEditing(false); setErr(''); }}
                className="h-[30px] px-3 rounded-[8px] text-[12px] text-[#8F877B] hover:text-[#F4F1EA] transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={save}
                disabled={saving}
                className="h-[30px] px-3.5 rounded-[8px] text-[12px] font-semibold transition-colors
                           bg-gradient-to-b from-[#F4F1EA] to-[#CFCAC0] text-[#141210]
                           hover:from-white hover:to-[#DED9CE] disabled:opacity-50"
              >
                {saving ? 'Enregistrement…' : 'Enregistrer'}
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
